import type { Action } from "@judo/actions";
import { type ReactNode, createContext, useContext } from "react";

/**
 * Dispatch function type for actions.
 */
export type DispatchFn = (action: Action) => Promise<unknown>;

/**
 * Dispatch context type.
 */
export interface DispatchContextType {
	/** Dispatch an action */
	dispatch: DispatchFn;
}

const DispatchContext = createContext<DispatchContextType | null>(null);

/**
 * Hook to access dispatch context.
 *
 * @returns DispatchContextType
 * @throws Error if used outside DispatchProvider
 */
export function useDispatch(): DispatchContextType {
	const context = useContext(DispatchContext);
	if (!context) {
		throw new Error("useDispatch must be used within DispatchProvider");
	}
	return context;
}

/**
 * Hook to access dispatch context (optional).
 *
 * @returns DispatchContextType | null
 */
export function useDispatchOptional(): DispatchContextType | null {
	return useContext(DispatchContext);
}

/**
 * Props for DispatchProvider.
 */
export interface DispatchProviderProps {
	children: ReactNode;
	dispatch: DispatchFn;
}

/**
 * Provider for dispatch context.
 * Provides the dispatch function to all child components.
 */
export function DispatchProvider({ children, dispatch }: DispatchProviderProps) {
	const value: DispatchContextType = { dispatch };

	return <DispatchContext.Provider value={value}>{children}</DispatchContext.Provider>;
}
